import React, { useId } from 'react';
import { clsx } from 'clsx';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  /** Label text for the group */
  label?: string;
  /** Available options */
  options: RadioOption[];
  /** Currently selected value */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Layout orientation */
  orientation?: 'vertical' | 'horizontal';
  /** Render options as selectable cards */
  variant?: 'default' | 'cards';
  /** Error message */
  error?: string;
  /** Additional CSS classes */
  className?: string;
  /** Name attribute for the radio group */
  name?: string;
  /**
   * Densidade do alvo de toque. `touch` garante altura mínima de 48px por opção
   * (iOS HIG: ≥44px) para telas usadas no celular, em campo — ex.: preenchimento
   * de checklist. `default` mantém EXATAMENTE a densidade histórica.
   */
  size?: 'default' | 'touch';
  /**
   * Prefixo de `data-testid` (CLAUDE.md SEMPRE #15): o container recebe o valor
   * e cada opção recebe `${testId}-${option.value}`.
   */
  'data-testid'?: string;
}

/** Altura mínima por opção no modo `touch` (iOS HIG ≥44px, com folga). */
const TOUCH_MIN_HEIGHT = 48;

/** Radio custom do DS (`.radio`): círculo com dot preenchido na cor primária. */
const RadioDot: React.FC<{ selected: boolean }> = ({ selected }) => (
  <span
    className="flex items-center justify-center rounded-full flex-none transition-colors peer-focus-visible:shadow-[0_0_0_3px_var(--ring-color)]"
    style={{
      width: 18,
      height: 18,
      marginTop: 1,
      border: `1.6px solid ${selected ? 'var(--theme-primary)' : 'var(--theme-input-border)'}`,
      backgroundColor: 'var(--theme-input-background)',
    }}
  >
    {selected && (
      <span className="rounded-full" style={{ width: 9, height: 9, backgroundColor: 'var(--theme-primary)' }} />
    )}
  </span>
);

/**
 * Shared RadioGroup component with vertical/horizontal layouts and card variant.
 * Usa o radio custom do DS (círculo + dot); o `<input type="radio">` real fica
 * sr-only para acessibilidade/teclado.
 *
 * @example
 * ```tsx
 * <RadioGroup
 *   label="Escopo de permissão"
 *   options={[
 *     { value: 'own', label: 'Próprio', description: 'Apenas seus dados' },
 *     { value: 'team', label: 'Equipe', description: 'Dados da equipe' },
 *     { value: 'all', label: 'Todos', description: 'Todos os dados' },
 *   ]}
 *   value={scope}
 *   onChange={setScope}
 *   variant="cards"
 * />
 * ```
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({
  label,
  options,
  value,
  onChange,
  orientation = 'vertical',
  variant = 'default',
  error,
  className,
  name: propName,
  size = 'default',
  'data-testid': testId,
}) => {
  const generatedName = useId();
  const groupName = propName || generatedName;
  const touch = size === 'touch';

  return (
    <div className={className} data-testid={testId}>
      {label && (
        <p className="text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
          {label}
        </p>
      )}

      <div
        className={clsx(
          variant === 'cards' ? 'gap-2' : 'gap-3',
          orientation === 'horizontal' ? 'flex flex-wrap' : 'flex flex-col'
        )}
      >
        {options.map((option) => {
          const isSelected = value === option.value;
          const optionId = `${groupName}-${option.value}`;

          if (variant === 'cards') {
            return (
              <label
                key={option.value}
                htmlFor={optionId}
                data-testid={testId ? `${testId}-${option.value}` : undefined}
                // `relative` é obrigatório: o <input> real é `sr-only` (position:absolute).
                // Sem um pai posicionado, o bloco de contenção dele é a viewport — ele
                // escapa de qualquer container com scroll e ESTICA o documento até a
                // posição estática que teria no fluxo (Configurações › Gamificação
                // ficou com 1390px de altura numa janela de 900px, 2026-08-22).
                className={clsx(
                  'relative flex gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors',
                  touch ? 'items-center' : 'items-start',
                  option.disabled && 'opacity-50 cursor-not-allowed',
                  orientation === 'horizontal' && 'flex-1'
                )}
                style={{
                  borderColor: isSelected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
                  backgroundColor: isSelected ? 'var(--theme-primary-background)' : 'transparent',
                  ...(touch ? { minHeight: TOUCH_MIN_HEIGHT } : {}),
                }}
              >
                <input
                  type="radio"
                  id={optionId}
                  name={groupName}
                  value={option.value}
                  checked={isSelected}
                  onChange={() => onChange?.(option.value)}
                  disabled={option.disabled}
                  className="peer sr-only"
                />
                <RadioDot selected={isSelected} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
                    {option.label}
                  </p>
                  {option.description && (
                    <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                      {option.description}
                    </p>
                  )}
                </div>
              </label>
            );
          }

          return (
            <label
              key={option.value}
              htmlFor={optionId}
              data-testid={testId ? `${testId}-${option.value}` : undefined}
              className={clsx(
                'relative flex gap-2.5 cursor-pointer',
                touch ? 'items-center' : 'items-start',
                option.disabled && 'opacity-50 cursor-not-allowed'
              )}
              style={touch ? { minHeight: TOUCH_MIN_HEIGHT } : undefined}
            >
              <input
                type="radio"
                id={optionId}
                name={groupName}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange?.(option.value)}
                disabled={option.disabled}
                className="peer sr-only"
              />
              <RadioDot selected={isSelected} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
                  {option.label}
                </p>
                {option.description && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                    {option.description}
                  </p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {error && (
        <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

RadioGroup.displayName = 'RadioGroup';
